/**
 * Shop quote-card field registry: which rows a product's card shows, and what
 * each row is called.
 *
 * Each product has one layout file in this folder, built from the fields in
 * ./fieldBank.ts and worded to match the carrier's illustration. The table
 * below maps fundingPolicyType to its layout.
 *
 * getQuoteCardSections returns null for a product that has no layout yet. The
 * caller (the client's getProductDetailsSections) then falls back to the older
 * shared card, which decides rows by carrier name. To move a product over, add
 * its layout file and one line to the table.
 */

import { FundingPolicyType } from '../appModel';
import { careMattersAnnuityLayout } from './careMattersAnnuity';
import { careMattersIILayout } from './careMattersII';
import type { QuoteCardEntry, QuoteCardLayout, QuoteCardQuote, QuoteCardSection } from './types';

export { formatBenefitPaymentType } from './fieldBank';
export type { QuoteCardEntry, QuoteCardQuote, QuoteCardSection } from './types';

const QUOTE_CARD_LAYOUTS: Partial<Record<FundingPolicyType, QuoteCardLayout>> = {
  [FundingPolicyType.careMattersTwo]: careMattersIILayout,
  [FundingPolicyType.careMattersAnnuity]: careMattersAnnuityLayout,
  [FundingPolicyType.jointCareMattersAnnuity]: careMattersAnnuityLayout,
};

export function getQuoteCardSections(quote: QuoteCardQuote): QuoteCardSection[] | null {
  const layout = QUOTE_CARD_LAYOUTS[quote.fundingPolicyType as FundingPolicyType];
  if (!layout) return null;

  return layout.map(({ title, fields }) => ({
    title,
    items: fields
      .map(field => field(quote))
      .filter((entry): entry is QuoteCardEntry => entry !== null),
  }));
}
